import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';

import {
  BillingType,
  FreeAgentUserDetails,
  Project,
  ProjectFormData,
  initialProjectFormData,
} from '../types';

const billingTypeLabels: Record<BillingType, string> = {
  time_and_materials: 'Time & Materials',
  fixed: 'Fixed',
};

interface FreeAgentProjectOption {
  id?: string;
  url: string;
  name?: string | null;
  jiraKey?: string | null;
}

interface FreeAgentUserOption extends FreeAgentUserDetails {
  id?: string;
  url: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string; error?: string; detail?: string }
      | string
      | undefined;

    if (!data) {
      return fallback;
    }

    if (typeof data === 'string') {
      return data;
    }

    return data.message || data.error || data.detail || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formData, setFormData] = useState<ProjectFormData>(initialProjectFormData);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);
  const [viewingProjectId, setViewingProjectId] = useState<number | null>(null);
  const [deletingProjectId, setDeletingProjectId] = useState<number | null>(null);
  const [freeAgentProjects, setFreeAgentProjects] = useState<FreeAgentProjectOption[]>([]);
  const [freeAgentProjectsLoading, setFreeAgentProjectsLoading] = useState(false);
  const [freeAgentProjectsError, setFreeAgentProjectsError] = useState<string | null>(null);
  const [freeAgentUsers, setFreeAgentUsers] = useState<FreeAgentUserOption[]>([]);
  const [freeAgentUsersLoading, setFreeAgentUsersLoading] = useState(false);
  const [freeAgentUsersError, setFreeAgentUsersError] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const username = process.env.REACT_APP_API_USERNAME ?? '';
  const password = process.env.REACT_APP_API_PASSWORD ?? '';

  const authHeader = useMemo(() => {
    if (!username || !password) {
      return null;
    }

    return `Basic ${btoa(`${username}:${password}`)}`;
  }, [username, password]);

  const fetchProjects = useCallback(async () => {
    if (!apiUrl) {
      setError('API URL is not configured.');
      setProjects([]);
      return;
    }

    if (!authHeader) {
      setError('API credentials are not configured.');
      setProjects([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<Project[]>(`${apiUrl}/projects`, {
        headers: { Authorization: authHeader },
      });
      setProjects(response.data);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load projects.'));
    } finally {
      setLoading(false);
    }
  }, [apiUrl, authHeader]);

  const fetchFreeAgentProjects = useCallback(async () => {
    if (!apiUrl || !authHeader) {
      return;
    }

    setFreeAgentProjectsLoading(true);
    setFreeAgentProjectsError(null);

    try {
      const response = await axios.get<FreeAgentProjectOption[]>(`${apiUrl}/freeagent-projects`, {
        headers: { Authorization: authHeader },
        params: { onlyActive: true },
      });
      setFreeAgentProjects(response.data);
    } catch (err) {
      setFreeAgentProjects([]);
      setFreeAgentProjectsError(getErrorMessage(err, 'Failed to load FreeAgent projects.'));
    } finally {
      setFreeAgentProjectsLoading(false);
    }
  }, [apiUrl, authHeader]);

  const fetchFreeAgentUsers = useCallback(async () => {
    if (!apiUrl || !authHeader) {
      return;
    }

    setFreeAgentUsersLoading(true);
    setFreeAgentUsersError(null);

    try {
      const response = await axios.get<FreeAgentUserOption[]>(`${apiUrl}/freeagent-users`, {
        headers: { Authorization: authHeader },
      });
      setFreeAgentUsers(response.data);
    } catch (err) {
      setFreeAgentUsers([]);
      setFreeAgentUsersError(getErrorMessage(err, 'Failed to load FreeAgent users.'));
    } finally {
      setFreeAgentUsersLoading(false);
    }
  }, [apiUrl, authHeader]);

  const ensureFreeAgentMetadata = useCallback(() => {
    if (!freeAgentProjectsLoading && freeAgentProjects.length === 0) {
      void fetchFreeAgentProjects();
    }

    if (!freeAgentUsersLoading && freeAgentUsers.length === 0) {
      void fetchFreeAgentUsers();
    }
  }, [
    fetchFreeAgentProjects,
    fetchFreeAgentUsers,
    freeAgentProjects.length,
    freeAgentProjectsLoading,
    freeAgentUsers.length,
    freeAgentUsersLoading,
  ]);

  useEffect(() => {
    void fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (!apiUrl || !authHeader) {
      return;
    }

    if (!freeAgentProjectsLoading && freeAgentProjects.length === 0) {
      void fetchFreeAgentProjects();
    }

    if (!freeAgentUsersLoading && freeAgentUsers.length === 0) {
      void fetchFreeAgentUsers();
    }
  }, [
    apiUrl,
    authHeader,
    fetchFreeAgentProjects,
    fetchFreeAgentUsers,
    freeAgentProjects.length,
    freeAgentProjectsLoading,
    freeAgentUsers.length,
    freeAgentUsersLoading,
  ]);

  const formatBillingType = (type?: BillingType | null) => {
    if (!type) {
      return '—';
    }

    return billingTypeLabels[type];
  };

  const getFreeAgentUserDisplayName = (user?: FreeAgentUserDetails | null) => {
    if (!user) {
      return '';
    }

    const providedName = user.name?.trim();
    const firstName = user.firstName?.trim();
    const lastName = user.lastName?.trim();
    const combinedName = [firstName, lastName].filter(Boolean).join(' ').trim();

    return providedName || combinedName || user.email?.trim() || '';
  };

  const renderNameWithUrl = (name?: string | null, url?: string | null): React.ReactNode => {
    const trimmedName = name?.trim();
    const trimmedUrl = url?.trim();

    if (!trimmedName && !trimmedUrl) {
      return '—';
    }

    if (!trimmedUrl) {
      return trimmedName;
    }

    return (
      <div className="d-flex flex-column text-break">
        <a href={trimmedUrl} target="_blank" rel="noreferrer" className="text-break">
          {trimmedName || trimmedUrl}
        </a>
        {trimmedName && (
          <span className="text-muted small text-break">{trimmedUrl}</span>
        )}
      </div>
    );
  };

  const freeAgentUsersByUrl = useMemo(() => {
    const map = new Map<string, FreeAgentUserOption>();

    freeAgentUsers.forEach((user) => {
      const trimmedUrl = user.url?.trim();

      if (trimmedUrl) {
        map.set(trimmedUrl, user);
      }
    });

    return map;
  }, [freeAgentUsers]);

  const resolveProjectManagerName = (
    manager?: FreeAgentUserDetails | null,
    managerUrl?: string | null
  ) => {
    const directName = getFreeAgentUserDisplayName(manager);

    if (directName) {
      return directName;
    }

    const trimmedUrl = managerUrl?.trim();

    if (!trimmedUrl) {
      return '';
    }

    const fallbackUser = freeAgentUsersByUrl.get(trimmedUrl) ?? null;

    return getFreeAgentUserDisplayName(fallbackUser);
  };

  const freeAgentProjectSelectOptions = useMemo<SelectOption[]>(() => {
    const options = freeAgentProjects
      .filter((project) => Boolean(project.url))
      .map<SelectOption>((project) => {
        const trimmedName = project.name?.trim();
        const trimmedJiraKey = project.jiraKey?.trim();
        const id = project.id?.trim() ?? '';
        const baseLabel = trimmedName || trimmedJiraKey || project.url;
        const detailParts: string[] = [];

        if (trimmedJiraKey && trimmedJiraKey !== baseLabel && (!trimmedName || !trimmedName.includes(trimmedJiraKey))) {
          detailParts.push(trimmedJiraKey);
        }

        if (id) {
          detailParts.push(`ID: ${id}`);
        }

        const details = detailParts.length > 0 ? ` (${detailParts.join(' • ')})` : '';

        return {
          value: project.url,
          label: `${baseLabel}${details}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

    const trimmedSelectedValue = formData.freeagentProjectUrl.trim();

    if (trimmedSelectedValue && !options.some((option) => option.value === trimmedSelectedValue)) {
      options.unshift({
        value: trimmedSelectedValue,
        label: `Current value (${trimmedSelectedValue})`,
      });
    }

    return options;
  }, [freeAgentProjects, formData.freeagentProjectUrl]);

  const freeAgentUserSelectOptions = useMemo<SelectOption[]>(() => {
    const options = freeAgentUsers
      .filter((user) => Boolean(user.url))
      .map<SelectOption>((user) => {
        const providedName = user.name?.trim();
        const firstName = user.firstName?.trim();
        const lastName = user.lastName?.trim();
        const fullName = providedName || [firstName, lastName].filter(Boolean).join(' ').trim();
        const email = user.email?.trim();
        const baseLabel = fullName || email || user.url;
        const detailParts: string[] = [];

        if (email && email !== baseLabel) {
          detailParts.push(email);
        }

        const id = user.id?.trim();

        if (id) {
          detailParts.push(`ID: ${id}`);
        }

        const details = detailParts.length > 0 ? ` (${detailParts.join(' • ')})` : '';

        return {
          value: user.url,
          label: `${baseLabel}${details}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

    const trimmedSelectedValue = formData.projectManagerUrl.trim();

    if (trimmedSelectedValue && !options.some((option) => option.value === trimmedSelectedValue)) {
      options.unshift({
        value: trimmedSelectedValue,
        label: `Current value (${trimmedSelectedValue})`,
      });
    }

    return options;
  }, [freeAgentUsers, formData.projectManagerUrl]);

  const openCreateModal = () => {
    setFormData(initialProjectFormData);
    setEditingProjectId(null);
    setFormError(null);
    ensureFreeAgentMetadata();
    setShowFormModal(true);
  };

  const openEditModal = (project: Project) => {
    setEditingProjectId(project.projectId);
    setFormError(null);
    setFormData({
      freeagentProjectUrl: project.freeagentProjectUrl ?? '',
      billingType: project.billingType === 'fixed' ? 'fixed' : 'time_and_materials',
      projectManagerUrl: project.projectManagerUrl ?? '',
    });
    ensureFreeAgentMetadata();
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setFormError(null);
    setFormData(initialProjectFormData);
    setEditingProjectId(null);
  };

  const handleBillingTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as BillingType;
    setFormData((prev) => ({ ...prev, billingType: value }));
  };

  const handleProjectSelectionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setFormData((prev) => ({ ...prev, freeagentProjectUrl: value }));
  };

  const handleProjectManagerSelectionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setFormData((prev) => ({ ...prev, projectManagerUrl: value }));
  };

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!apiUrl) {
      setFormError('API URL is not configured.');
      return;
    }

    if (!authHeader) {
      setFormError('API credentials are not configured.');
      return;
    }

    const trimmedFreeagentUrl = formData.freeagentProjectUrl.trim();
    const trimmedProjectManagerUrl = formData.projectManagerUrl.trim();

    if (!trimmedFreeagentUrl) {
      setFormError('FreeAgent project URL is required.');
      return;
    }

    if (!trimmedProjectManagerUrl) {
      setFormError('Project manager URL is required.');
      return;
    }

    setSaving(true);
    setFormError(null);

    const payload = {
      freeagentProjectUrl: trimmedFreeagentUrl,
      billingType: formData.billingType,
      projectManagerUrl: trimmedProjectManagerUrl,
    };

    try {
      if (editingProjectId) {
        await axios.put<Project>(`${apiUrl}/projects/${editingProjectId}`, payload, {
          headers: { Authorization: authHeader },
        });
      } else {
        await axios.post<Project>(`${apiUrl}/projects`, payload, {
          headers: { Authorization: authHeader },
        });
      }

      await fetchProjects();
      closeFormModal();
    } catch (err) {
      setFormError(
        getErrorMessage(
          err,
          editingProjectId ? 'Failed to update project.' : 'Failed to create project.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const openViewModal = async (projectId: number) => {
    if (!apiUrl) {
      setError('API URL is not configured.');
      return;
    }

    if (!authHeader) {
      setError('API credentials are not configured.');
      return;
    }

    setSelectedProject(null);
    setViewError(null);
    setViewModalVisible(true);
    setViewLoading(true);
    setViewingProjectId(projectId);

    try {
      const response = await axios.get<Project>(`${apiUrl}/projects/${projectId}`, {
        headers: { Authorization: authHeader },
      });
      setSelectedProject(response.data);
    } catch (err) {
      setViewError(getErrorMessage(err, 'Failed to load project details.'));
    } finally {
      setViewLoading(false);
      setViewingProjectId(null);
    }
  };

  const closeViewModal = () => {
    setViewModalVisible(false);
    setSelectedProject(null);
    setViewError(null);
  };

  const handleDeleteProject = async (projectId: number) => {
    if (!apiUrl) {
      setError('API URL is not configured.');
      return;
    }

    if (!authHeader) {
      setError('API credentials are not configured.');
      return;
    }

    // eslint-disable-next-line no-alert
    const confirmDelete = window.confirm('Are you sure you want to delete this project?');

    if (!confirmDelete) {
      return;
    }

    setDeletingProjectId(projectId);
    setError(null);

    try {
      await axios.delete(`${apiUrl}/projects/${projectId}`, {
        headers: { Authorization: authHeader },
      });

      setProjects((prev) => prev.filter((project) => project.projectId !== projectId));

      if (selectedProject?.projectId === projectId) {
        closeViewModal();
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete project.'));
    } finally {
      setDeletingProjectId(null);
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
        <h2 className="mb-3 mb-md-0">Projects</h2>
        <div>
          <Button
            variant="secondary"
            className="me-2"
            onClick={() => void fetchProjects()}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button variant="primary" onClick={openCreateModal}>
            New Project
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center align-items-center my-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th style={{ minWidth: '110px' }}>ID</th>
              <th>Project Name</th>
              <th>Project Manager</th>
              <th>Billing Type</th>
              <th style={{ minWidth: '220px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted">
                  No projects found.
                </td>
              </tr>
            ) : (
              projects.map((project) => (
                <tr key={project.projectId}>
                  <td>{project.projectId}</td>
                  <td>
                    {renderNameWithUrl(project.freeagentProject?.name, project.freeagentProjectUrl)}
                  </td>
                  <td>
                    {renderNameWithUrl(
                      resolveProjectManagerName(project.projectManager, project.projectManagerUrl),
                      project.projectManagerUrl
                    )}
                  </td>
                  <td>{formatBillingType(project.billingType)}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => void openViewModal(project.projectId)}
                        disabled={viewingProjectId === project.projectId}
                      >
                        {viewingProjectId === project.projectId ? (
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          'View'
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => openEditModal(project)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => void handleDeleteProject(project.projectId)}
                        disabled={deletingProjectId === project.projectId}
                      >
                        {deletingProjectId === project.projectId ? (
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          'Delete'
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <Form onSubmit={handleFormSubmit} noValidate>
          <Modal.Header closeButton>
            <Modal.Title>{editingProjectId ? 'Edit Project' : 'New Project'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}

            <Form.Group className="mb-3" controlId="projectFreeagentUrl">
              <Form.Label>FreeAgent Project</Form.Label>
              <Form.Select
                name="freeagentProjectUrl"
                value={formData.freeagentProjectUrl}
                onChange={handleProjectSelectionChange}
                disabled={freeAgentProjectsLoading && freeAgentProjectSelectOptions.length === 0}
                required
              >
                <option value="">
                  {freeAgentProjectsLoading && freeAgentProjectSelectOptions.length === 0
                    ? 'Loading projects...'
                    : 'Select a FreeAgent project'}
                </option>
                {freeAgentProjectSelectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text muted>Choose the FreeAgent project associated with this record.</Form.Text>
              {freeAgentProjectsError && (
                <Alert variant="warning" className="mt-2 mb-0">
                  {freeAgentProjectsError}
                </Alert>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="projectBillingType">
              <Form.Label>Billing Type</Form.Label>
              <Form.Select
                name="billingType"
                value={formData.billingType}
                onChange={handleBillingTypeChange}
              >
                <option value="time_and_materials">Time & Materials</option>
                <option value="fixed">Fixed</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-0" controlId="projectManagerUrl">
              <Form.Label>Project Manager</Form.Label>
              <Form.Select
                name="projectManagerUrl"
                value={formData.projectManagerUrl}
                onChange={handleProjectManagerSelectionChange}
                disabled={freeAgentUsersLoading && freeAgentUserSelectOptions.length === 0}
                required
              >
                <option value="">
                  {freeAgentUsersLoading && freeAgentUserSelectOptions.length === 0
                    ? 'Loading users...'
                    : 'Select a FreeAgent user'}
                </option>
                {freeAgentUserSelectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text muted>Select the FreeAgent user who manages this project.</Form.Text>
              {freeAgentUsersError && (
                <Alert variant="warning" className="mt-2 mb-0">
                  {freeAgentUsersError}
                </Alert>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeFormModal} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Project'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={viewModalVisible} onHide={closeViewModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Project Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewLoading && (
            <div className="d-flex justify-content-center my-3">
              <Spinner animation="border" role="status" />
            </div>
          )}

          {viewError && <Alert variant="danger">{viewError}</Alert>}

          {!viewLoading && !viewError && selectedProject && (
            <div>
              <dl className="row">
                <dt className="col-sm-4">Project ID</dt>
                <dd className="col-sm-8">{selectedProject.projectId}</dd>

                <dt className="col-sm-4">Billing Type</dt>
                <dd className="col-sm-8">{formatBillingType(selectedProject.billingType)}</dd>

                <dt className="col-sm-4">FreeAgent URL</dt>
                <dd className="col-sm-8">
                  {selectedProject.freeagentProjectUrl ? (
                    <a href={selectedProject.freeagentProjectUrl} target="_blank" rel="noreferrer">
                      {selectedProject.freeagentProjectUrl}
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>

                <dt className="col-sm-4">Project Manager</dt>
                <dd className="col-sm-8">
                  {renderNameWithUrl(
                    resolveProjectManagerName(
                      selectedProject.projectManager,
                      selectedProject.projectManagerUrl
                    ),
                    selectedProject.projectManagerUrl
                  )}
                </dd>

                <dt className="col-sm-4">Created</dt>
                <dd className="col-sm-8">{selectedProject.createdAt ?? '—'}</dd>

                <dt className="col-sm-4">Last Updated</dt>
                <dd className="col-sm-8">{selectedProject.updatedAt ?? '—'}</dd>
              </dl>

              {selectedProject.freeagentProject && (
                <div>
                  <h5 className="mt-4">FreeAgent Project Snapshot</h5>
                  <dl className="row">
                    <dt className="col-sm-4">Name</dt>
                    <dd className="col-sm-8">{selectedProject.freeagentProject?.name ?? '—'}</dd>

                    <dt className="col-sm-4">Status</dt>
                    <dd className="col-sm-8">{selectedProject.freeagentProject?.status ?? '—'}</dd>

                    <dt className="col-sm-4">Contact</dt>
                    <dd className="col-sm-8">{selectedProject.freeagentProject?.contactName ?? '—'}</dd>

                    <dt className="col-sm-4">Currency</dt>
                    <dd className="col-sm-8">{selectedProject.freeagentProject?.currency ?? '—'}</dd>

                    <dt className="col-sm-4">Budget</dt>
                    <dd className="col-sm-8">
                      {selectedProject.freeagentProject?.budget != null
                        ? `${selectedProject.freeagentProject.budget} ${selectedProject.freeagentProject?.budgetUnits ?? ''}`.trim()
                        : '—'}
                    </dd>

                    <dt className="col-sm-4">Hours per Day</dt>
                    <dd className="col-sm-8">
                      {selectedProject.freeagentProject?.hoursPerDay ?? '—'}
                    </dd>

                    <dt className="col-sm-4">Billing Rate</dt>
                    <dd className="col-sm-8">
                      {selectedProject.freeagentProject?.normalBillingRate != null
                        ? selectedProject.freeagentProject.normalBillingRate
                        : '—'}
                    </dd>

                    <dt className="col-sm-4">Billing Period</dt>
                    <dd className="col-sm-8">{selectedProject.freeagentProject?.billingPeriod ?? '—'}</dd>

                    <dt className="col-sm-4">Starts On</dt>
                    <dd className="col-sm-8">{selectedProject.freeagentProject?.startsOn ?? '—'}</dd>

                    <dt className="col-sm-4">Ends On</dt>
                    <dd className="col-sm-8">{selectedProject.freeagentProject?.endsOn ?? '—'}</dd>

                    <dt className="col-sm-4">Uses Invoice Sequence</dt>
                    <dd className="col-sm-8">
                      {selectedProject.freeagentProject?.usesProjectInvoiceSequence == null
                        ? '—'
                        : selectedProject.freeagentProject.usesProjectInvoiceSequence
                        ? 'Yes'
                        : 'No'}
                    </dd>

                    <dt className="col-sm-4">Include Unbilled Time</dt>
                    <dd className="col-sm-8">
                      {selectedProject.freeagentProject?.includeUnbilledTimeInProfitability == null
                        ? '—'
                        : selectedProject.freeagentProject.includeUnbilledTimeInProfitability
                        ? 'Yes'
                        : 'No'}
                    </dd>

                    <dt className="col-sm-4">IR35</dt>
                    <dd className="col-sm-8">
                      {selectedProject.freeagentProject?.isIr35 == null
                        ? '—'
                        : selectedProject.freeagentProject.isIr35
                        ? 'Yes'
                        : 'No'}
                    </dd>

                    <dt className="col-sm-4">FreeAgent URL</dt>
                    <dd className="col-sm-8">
                      {selectedProject.freeagentProject?.url ? (
                        <a
                          href={selectedProject.freeagentProject.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {selectedProject.freeagentProject.url}
                        </a>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </dl>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeViewModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProjectsPage;
